import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import mime from 'mime-types';
import moment from 'moment';
import { UserMessage } from '@bt-common/schemas/bt/user-message';

import { WaMessageType } from "@bt-common/interfaces/wa-store-types";
import {
    decodeCallerTokenFromMessageSecret,
    encodeUserMessageIdAsMessageSecret,
    matchMessageToUserMessageId,
} from "@bt-common/scheduling";


/**
 * Normalise the several shapes a messageSecret travels in (Uint8Array, plain
 * array — WWebJS's sendMessage poll path only accepts `Array.isArray` — or the
 * `{0:..,1:..}` object a serializer leaves behind) to a 32-byte Uint8Array, or
 * null when it is not a 32-byte secret.
 */
export function toMessageSecretBytes(raw: unknown): Uint8Array | null {
    if (!raw) return null;
    let bytes: Uint8Array | null = null;
    if (raw instanceof Uint8Array) bytes = raw;
    else if (Array.isArray(raw)) bytes = Uint8Array.from(raw as number[]);
    else if (typeof raw === 'object') bytes = Uint8Array.from(Object.values(raw as Record<string, number>));
    return bytes && bytes.length === 32 ? bytes : null;
}

/**
 * The messageSecret for a poll-creation built by WWebJS's sendMessage path:
 * the caller's secret in any transport shape (see toMessageSecretBytes), or —
 * only when none was supplied — WhatsApp-style random bytes (native behaviour
 * for polls we do not track). A supplied-but-malformed secret is an error, not
 * a silent random replacement (rev 3.3 §6.6 "one Chrome poll secret source").
 */
export function resolvePollCreationSecret(raw: unknown): Uint8Array {
    if (raw === undefined || raw === null) {
        return window.crypto.getRandomValues(new Uint8Array(32));
    }
    const bytes = toMessageSecretBytes(raw);
    if (!bytes) {
        throw new Error('[resolvePollCreationSecret] poll messageSecret must be 32 bytes');
    }
    return bytes;
}

export function getWaMessageType(message: UserMessage): WaMessageType | undefined {

    const contact = message.contactList?.[0];
    if (!contact || !contact.id) return;

    // Check if it's a newsletter chat
    const isNewsletter = chatKindOf(contact.id) === 'channel';

    // Handle poll messages
    if (message.isPoll) {
        return isNewsletter ? 'newsletterPoll' : 'poll';
    }

    // Handle media messages
    if (message.asset && message.asset.url && message.asset.url !== "") {
        if (isImage(message.asset)) {
            if (isNewsletter) {
                return 'newsletterImage';
            } else {
                return 'image';
            }
        } else {
            // Non-image attachments (video, audio, etc)
            if (isNewsletter) {
                return 'newsletterAttachmentWithoutImage';
            } else {
                return 'attachmentWithoutImage';
            }
        }
    }
    return 'text';
}

export function isImage(asset: any) {
    const mimeType = mime.lookup(asset.type ?? asset.name) || asset.type;
    return mimeType && mimeType.indexOf('image') > -1
}

export const initWaMessageOptions = (options: any) => {
    return {
        linkPreview: !!options.linkPreview ? true : undefined,
        sendAudioAsVoice: options.sendAudioAsVoice,
        sendVideoAsGif: options.sendVideoAsGif,
        sendMediaAsSticker: options.sendMediaAsSticker,
        sendMediaAsDocument: options.sendMediaAsDocument,
        caption: options.caption,
        quotedMessageId: options.quotedMessageId,
        parseVCards: options.parseVCards === false ? false : true,
        mentionedJidList: options.mentions || [],
        groupMentions: options.groupMentions,
        invokedBotWid: options.invokedBotWid,
        extraOptions: options.extra,
    };
}

export const isMessageSendAttemptedWas3MinutesAgo = (creationDate: string) => {
    const timeLimitInMinutes = 3
    if (moment().diff(moment(creationDate), 'minutes') < timeLimitInMinutes) {
        return false;
    }
    return true;
}

export const getThresholdTimestamp = (timeLimitInMinutes: number) => {
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const timeLimitInSeconds = timeLimitInMinutes * 60;
    return currentTimestamp - timeLimitInSeconds;
}

/**
 * Get a WhatsApp message from the in-memory Store by id.
 * Accepts either:
 *   - the full serialized id, e.g. "true_972...@c.us_3A78E4A7344ADB689A2D"
 *   - the stripped id alone, e.g. "3A78E4A7344ADB689A2D" (new WA format)
 *
 * Why: recent WA builds surface message ids in their stripped form in many
 * places (DOM, events). Store.Msg.get() is keyed on _serialized and returns
 * undefined for stripped ids, so we fall back to scanning Msg._models.
 */
export function getMsgFromStore(messageId: string): any | null {
    if (!messageId) return null;
    const msgStore = (window as any)?.getStore?.()?.Msg;
    if (!msgStore) return null;

    const direct = msgStore.get?.(messageId);
    if (direct) return direct;

    // Stripped form has no underscores; full _serialized always has at least two
    if (!messageId.includes('_')) {
        const models = msgStore._models || [];
        for (const m of models) {
            if (m?.id?.id === messageId) return m;
        }
    }

    return null;
}

/**
 * True only for a message THIS account sent. The store-check / ack / echo
 * matchers must never accept an inbound message: a customer can paste (or a
 * forward can carry) a message whose messageSecret decodes to one of our ids,
 * and matching it would falsely CONFIRM one of our pending sends (rev 3.3 §6.6
 * "fromMe filters in every store-check matcher"). Reads the MsgKey first
 * (Store.Msg models: `id.fromMe`), then a flat `fromMe` (serialized / wwebjs
 * Message shapes). Anything not explicitly `true` is treated as not ours.
 */
export function isWaMessageFromMe(waMessage: any): boolean {
    if (!waMessage) return false;
    const keyFromMe = waMessage?.id?.fromMe;
    if (typeof keyFromMe === 'boolean') return keyFromMe;
    return waMessage?.fromMe === true;
}

/**
 * The caller correlation token (`secret`) to SURFACE on a read-path projection,
 * or null. Gated twice:
 *   1. fromMe — rev 3.3 §6.6: only our own messages expose `secret`; an inbound
 *      message carrying a copied/forwarded secret must not look like our send;
 *   2. the caller-token marker — a native message's E2E secret is never decoded
 *      or forwarded (bt-common decodeCallerTokenFromMessageSecret).
 * Shared by the message-models projections and the page-realm listener payload.
 */
export function callerTokenOfOwnMessage(message: any): string | null {
    if (!isWaMessageFromMe(message)) return null;
    return callerTokenOfAnyMessage(message);
}

/**
 * Marker-gated caller token WITHOUT the fromMe gate — for the LISTENER payloads
 * sent to the backend only (rev 3.3 §6.6 + R1): the backend resolves the token
 * and verifies its binding (owner / chat / waId), which blocks forgeries, and a
 * second number of the same owner receives our replies as `fromMe:false`, so
 * the echo must still carry the token. Never use this for a public projection.
 */
export function callerTokenOfAnyMessage(message: any): string | null {
    try {
        const bytes = toMessageSecretBytes(message?.messageSecret ?? message?.messageContextInfo?.messageSecret);
        return bytes ? decodeCallerTokenFromMessageSecret(bytes) : null;
    } catch {
        return null;
    }
}

/** The chat a WA message lives in (serialized), or ''. */
function waMessageChatId(waMessage: any): string {
    const remote = waMessage?.id?.remote;
    const c = remote?._serialized ?? (typeof remote === 'string' ? remote : undefined) ?? waMessage?.chatId;
    return typeof c === 'string' ? c : '';
}

/**
 * The Chrome store-check matcher. The rule — only our own (fromMe) messages,
 * except a post in the TARGET channel (rev 3.3 §6.6) — and the secret decode
 * are bt-common's matchMessageToUserMessageId, shared with the Baileys pod.
 *
 * The token is read from the messageSecret, or from the WWebJS send-option tags
 * (`campaignId` / `comment`) our send paths set to the userMessage id — a tag hit
 * is this message whatever token its secret carries.
 *
 * @param targetChatId the chat the pending send targets. Enables the
 *   @newsletter exception ONLY when the matched message lives in that same
 *   channel (we are posting to it). Absent ⇒ fromMe-only.
 */
export const isUserMessageIdMatchToWaMessage = (messageId: string, waMessage: any, targetChatId?: string): { fromMe: boolean, remote: string, id: string, _serialized: string } | null => {
    if (!waMessage) return null;
    const taggedWithId = !!messageId && (waMessage.campaignId === messageId || waMessage.comment === messageId);
    const messageSecret = taggedWithId
        ? encodeUserMessageIdAsMessageSecret(messageId)
        : toMessageSecretBytes(waMessage.messageSecret) ?? undefined;
    const { matched } = matchMessageToUserMessageId(messageId, {
        messageSecret,
        fromMe: isWaMessageFromMe(waMessage),
        chatId: waMessageChatId(waMessage),
        waMessageKey: '',
        timestamp: 0,
        ack: 0,
    }, { targetChatId });
    return matched ? waMessage.id : null;
};
